import { toApiAffiliationTypes } from '../../utils/affiliationTypes';

import { format } from 'date-fns';

/**
 * Mapping of exclude filter flags to their corresponding API parameter names
 */
export const EXCLUDE_FILTER_MAP = {
  sources: {
    countriesExclude: 'origin_country_ids',
    languagesExclude: 'languages',
    stateAffiliatedExclude: 'country_ids',
    stateAffiliationTypesExclude: 'country_ids',
    highRiskExclude: 'discreditated',
    botsExclude: 'source_bots',
    sourceGroupExclude: 'source_group_id',
  },
  messages: {
    countriesExclude: 'source_origin_country_ids',
    languagesExclude: 'languages',
    stateAffiliatedExclude: 'source_state_affiliated_country_ids',
    stateAffiliationTypesExclude: 'source_affiliation_types',
    highRiskExclude: 'discreditated',
    botsExclude: 'source_bots',
    sourceGroupExclude: 'source_group_ids',
  },
};

/**
 * Builds common URL parameters shared between sources and messages endpoints
 * @param {Object} config - Configuration object with filter values
 * @param {Object} options - Additional options like parameter name mappings
 * @returns {URLSearchParams}
 */
export const buildCommonParams = (config, options = {}) => {
  const {
    narrativeId,
    sorting,
    widgetName,
    sourceType,
    sourceSubType,
    stateAffiliatedID,
    affiliatedTypes,
    platform,
    countryID,
    audience,
    sourceGroup,
    tags,
    languages,
    eventIds,
    opinionIds,
    sentiment,
    contentTypes,
    start,
    end,
    page = 1,
    size = 10,
  } = config;

  const {
    audienceParamPrefix = '',
    countryIdParam = 'country_ids',
  } = options;

  const urlParams = new URLSearchParams();

  // Pagination
  urlParams.set('size', String(size));
  urlParams.set('page', String(page));

  // Narrative ID
  if (narrativeId) {
    urlParams.set('narrative_id', narrativeId);
  }

  // Widget name for tracking
  if (widgetName) {
    urlParams.set('widget_name', `content_modal_${widgetName}`);
  }

  // Sorting
  if (sorting) {
    urlParams.set('sorting', sorting);
  }

  // Source type filter
  if (sourceType?.value) {
    urlParams.set(
      sourceType.value,
      sourceType.bool === null ? true : sourceType.bool
    );
  }

  // Source sub-type filter
  if (sourceSubType) {
    if (sourceType?.value === 'discreditated') {
      urlParams.set('entity_type', sourceSubType);
    } else {
      urlParams.set(sourceSubType, true);
    }
  }

  // State affiliated country IDs
  if (stateAffiliatedID?.length > 0) {
    stateAffiliatedID.forEach((id) => urlParams.append(countryIdParam, id));
  }

  // Affiliation types
  if (affiliatedTypes?.length > 0) {
    toApiAffiliationTypes(affiliatedTypes).forEach((type) => urlParams.append('affiliation_types', type));
  }

  // Platform filter
  if (platform?.length > 0) {
    platform.forEach((p) => urlParams.append('source_types', p.value));
  }

  // Country filter
  if (countryID?.length > 0) {
    countryID.forEach((country) => urlParams.append('origin_country_ids', country.value));
  }

  // Audience range
  if (audience) {
    urlParams.set(`${audienceParamPrefix}audience_lte`, audience[1]);
    urlParams.set(`${audienceParamPrefix}audience_gte`, audience[0]);
  }

  // Source groups
  if (sourceGroup?.length > 0) {
    sourceGroup.forEach((groupId) => urlParams.append('source_group_id', groupId));
  }

  // Tags (ltree path strings; backend matches descendants)
  if (tags?.length > 0) {
    tags.forEach((tag) => urlParams.append('tags', tag));
  }

  // Languages
  if (languages?.length > 0) {
    languages.forEach((langCode) => urlParams.append('languages', langCode));
  }

  // Event IDs
  if (eventIds?.length > 0) {
    eventIds.forEach((id) => urlParams.append('topic_modeling_event_ids', id));
  }

  // Opinion IDs
  if (opinionIds?.length > 0) {
    opinionIds.forEach((id) => urlParams.append('topic_modeling_opinion_ids', id));
  }

  // Sentiment
  if (sentiment?.length > 0) {
    sentiment.forEach((s) => urlParams.append('sentiment', s));
  }

  // Content types
  if (contentTypes?.length > 0) {
    contentTypes.forEach((ct) => urlParams.append('content_types', ct));
  }

  // Date range
  if (start) {
    urlParams.append('start', format(start, 'yyyy-LL-dd 00:00:00'));
  }
  if (end) {
    urlParams.append('end', format(end, 'yyyy-LL-dd 23:59:59'));
  }

  return urlParams;
};

/**
 * Builds and appends exclude filter parameters
 * @param {URLSearchParams} urlParams - URL params to append to
 * @param {Object} excludeFlags - Object with exclude flag values
 * @param {string} type - Either 'sources' or 'messages'
 */
export const appendExcludeFilters = (urlParams, excludeFlags, type) => {
  const filterMap = EXCLUDE_FILTER_MAP[type];
  const excludingFilters = [];

  Object.entries(excludeFlags).forEach(([key, value]) => {
    if (value && filterMap[key]) {
      excludingFilters.push(filterMap[key]);
    }
  });

  // Special handling for source group exclude
  if (excludeFlags.sourceGroupExclude) {
    urlParams.append('include_unclassified', false);
  }

  if (excludingFilters.length > 0) {
    excludingFilters.forEach((filter) => urlParams.append('exclusive_filters', filter));
  }
};

/**
 * Builds the complete config object from individual filter values
 * This helps transition from the old parameter-heavy approach
 */
export const buildFetchConfig = ({
  narrativeId,
  sourceType,
  sourceSubType,
  platform,
  countryID,
  sorting,
  audience,
  sourceGroup,
  tags,
  sourceId,
  eventIds,
  opinionIds,
  sentiment,
  start,
  end,
  contentTypes,
  languages,
  stateAffiliatedID,
  affiliatedTypes,
  widgetName,
  page = 1,
}) => ({
  narrativeId,
  sourceType,
  sourceSubType,
  platform,
  countryID,
  sorting,
  audience,
  sourceGroup,
  tags,
  sourceId,
  eventIds,
  opinionIds,
  sentiment,
  start,
  end,
  contentTypes,
  languages,
  stateAffiliatedID,
  affiliatedTypes,
  widgetName,
  page,
});

/**
 * Builds exclude flags object from individual values
 */
export const buildExcludeFlags = ({
  countriesExclude,
  languagesExclude,
  stateAffiliatedExclude,
  stateAffiliationTypesExclude,
  highRiskExclude,
  botsExclude,
  sourceGroupExclude,
}) => ({
  countriesExclude,
  languagesExclude,
  stateAffiliatedExclude,
  stateAffiliationTypesExclude,
  highRiskExclude,
  botsExclude,
  sourceGroupExclude,
});
